import React, { FormEvent, ReactNode, useEffect, useRef, useMemo, useState } from 'react';
import { useParams, useRouter, useSearchParams} from 'next/navigation';
import {
  Configure,
  Hits,
  InfiniteHits,
  Pagination,
  useClearRefinements,
  useCurrentRefinements,
  useHits,
  usePagination,
  useSortBy,
  useRefinementList,
  useInfiniteHits,
  useInstantSearch,
} from 'react-instantsearch-hooks-web';
import { Category } from 'shared/types/product/Category';
import Wrapper from 'components/HOC/wrapper';
import { mapProduct } from 'helpers/algolia/map-product';
import { useFormat } from 'helpers/hooks/useFormat';
import AccumalativeTrace from './components/accumalative-trace';
import Breadcrumbs from './components/breadcrumb';
import CurrentRefinements from './components/current-refinements';
import DesktopFacets from './components/desktop-facets';
import MobileFacets from './components/mobile-facets';
import SearchHeader from './components/search-header';
import { FacetConfiguration, ILCategoryFacetFilters, IndependentLivingCategoriesFilters } from './types';
import ProductTile from '../product-tile';
import Search from 'components/commercetools-ui/atoms/search-algolia';
import DesktopSortFacets from './components/desktop-facets/sort-facets';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, tablet } from 'helpers/utils/screensizes';
import LocalizedIndex, { useLocalizedIndex } from 'providers/algolia/localized-index';
import ContentSearchItem from 'components/commercetools-ui/atoms/search-algolia/content-search';
import CustomStats from 'components/commercetools-ui/atoms/search-algolia/customstats';
import { localeMapping } from 'helpers/constants/localeMapping';
import useTranslation from 'providers/i18n/hooks/useTranslation';
 import { CATEGORY_IDs } from 'helpers/constants/products';
import ViewToggle from '../product-list/components/view-toggle';
import ScrollToTopButton from 'components/commercetools-ui/atoms/scroll-to-top';
interface Props {
  noContentSuggestions?: any;
  slug?: string;
  searchQuery?: string;
  categories: Category[];
  facetsConfiguration: Record<string, FacetConfiguration>;
  suggestions?: any;
}
interface NoResultProps {
  fallback: ReactNode;
}
function NoResultsBoundary({ children, fallback }: React.PropsWithChildren<NoResultProps>) {
  const { results } = useHits();
  if (results && !results?.__isArtificial && results?.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }
  return children;
}
function NoResults({ results, suggestions, searchQuery }: any) {
  const { formatMessage } = useFormat({ name: 'common' });
  const router = useRouter();

  const handleSuggestion = (suggestion: string) => {
    router.push(`/search?query=${encodeURIComponent(suggestion)}`, { shallow: true });
  };
  return (
    <div>
      {searchQuery && results?.nbHits === 0 ? (
        <div className="m-auto max-w-[900px] text-center lg:!text-left">
          <p className={`results-zero no-result-container mb-2`}>No products found</p>
          <div className="d-inline">
            {formatMessage({
              id: 'no.resultfound.query',
              defaultMessage: 'No search results are found for your query:',
            })}
            <span className="font-bold"> &quot;{searchQuery ? searchQuery : ''}&quot;</span>.
            {suggestions.length > 0 && (
              <p className="d-inline">
                {' '}
                {formatMessage({
                  id: 'did.you.mean',
                  defaultMessage: 'Did you mean?',
                })}{' '}
                {suggestions.map((suggestion: any, index: any) => (
                  <>
                    <span
                      role="button"
                      onClick={() => handleSuggestion(suggestion?.query)}
                      key={index}
                      className="text-decoration-underline font-medium"
                    >
                      {suggestion?.query}
                    </span>
                    <span className="text-decoration-none">{index < suggestions.length - 1 && ', '}</span>
                  </>
                ))}
              </p>
            )}
            {suggestions.length === 0 && (
              <p className="d-inline">
                {' '}
                {formatMessage({
                  id: 'search.popular.categories',
                  defaultMessage: 'You might want to search for popular categories',
                })}{' '}
                {['Tapware', 'Sanitaryware', 'Contura', 'Urbane II'].map((cat: any, index: any) => (
                  <>
                    <span
                      role="button"
                      onClick={() => handleSuggestion(cat)}
                      key={index}
                      className="text-decoration-underline font-medium"
                    >
                      {cat}
                    </span>
                    <span className="text-decoration-none">{index < cat.length - 1 && ', '}</span>
                  </>
                ))}
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className={`${results?.nbHits === 0 && 'results-zero'} no-result-container`}>No products found</p>
      )}
    </div>
  );
}
let globalView: 'grid' | 'list' = 'grid';
const ProductListAlgolia: React.FC<Props> = ({
  slug,
  searchQuery,
  categories,
  facetsConfiguration,
  noContentSuggestions,
  suggestions
}) => {
  const [isTabletSize] = useMediaQuery(desktop);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { locale, slug: categorySlug } = useParams();
  const searchParams = useSearchParams();
  const rangeParam = searchParams?.get('range') || undefined;
  const queryParam = searchParams?.get('query') || undefined;
  const isPdp = searchParams?.get('isPdp') || undefined;
  const [loading, setLoading] = useState(true);
  
  // Auto-apply range facet once if the query matches a range value
  const { refine: refineRange } = useRefinementList({ attribute: 'searchableAttributes.range' });
  useEffect(() => {
    setLoading(true);
    if (queryParam && isPdp && isPdp !== 'false') {
      refineRange(queryParam);
    }
  }, [queryParam, isPdp, refineRange]);
  
  const { refine } = usePagination();
  const { refine: clearAllRefinements } = useClearRefinements();
  const { items: currentRefinements } = useCurrentRefinements();
  const { indexName: productsIndex } = useLocalizedIndex({ type: 'products' });
  const { setUiState } = useInstantSearch();
  const localeUpperCase = locale.toUpperCase();
  const [persistedFacetFilters, setPersistedFacetFilters] = useState<string[] | string[][]>([]);
  const hasHydratedRefinements = useRef(false);
  const previousSearchQuery = useRef<string | undefined>(searchQuery);

  const storageKey = useMemo(() => `product_list_facet_filters_${locale}_${slug || 'all'}`, [locale, slug]);
  
  const { showMore, isLastPage } = useInfiniteHits();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { refine: clearSortRefinement } = useSortBy({
    items: [
      {
        label: formatProductMessage({ id: 'new-products', defaultMessage: 'New' }),
        value: productsIndex,
      },
      {
        label: formatProductMessage({ id: 'nameAsc', defaultMessage: 'Name A-Z' }),
        value: productsIndex.replace('-main', '') + '_name_asc',
      },
      {
        label: formatProductMessage({ id: 'nameDsc', defaultMessage: 'Name Z-A' }),
        value: productsIndex.replace('-main', '') + '_name_desc',
      },
    ],
  });
  const { items } = useCurrentRefinements();
  const { hits, results } = useHits();
  const hitsLength = hits.length;
  const totalHits = results ? results.nbHits : 0;
  const isKitchenLaundry = categorySlug?.includes('kitchen-laundry');
  const isBathroom = categorySlug?.includes('bathroom');
  const localeKey = localeMapping[locale];
  const isIndependentLiving = categorySlug?.includes('independent-living');
  const isClark = categorySlug?.includes('clark');
  const isDorf = categorySlug?.includes('dorf');
  

  const [currentView, setCurrentView] = useState<'grid' | 'list'>('grid');  
  const handleViewChange = (view: 'grid' | 'list') => {
    setCurrentView(view);
    if (view === 'grid') {
      globalView = 'grid';
    } else {
      globalView = 'list';
    }
  };


  useEffect(() => {
    setLoading(true);
    if (previousSearchQuery.current !== searchQuery) {
      // New search query detected - clear all filters
      if (searchQuery && previousSearchQuery.current !== undefined) {
        clearAllRefinements();
        // Clear localStorage for this page when starting a new search
        window.localStorage.removeItem(storageKey);
        setPersistedFacetFilters([]);
        hasHydratedRefinements.current = false;
      }
      previousSearchQuery.current = searchQuery;}
      }, [searchQuery, clearAllRefinements, storageKey]);
useEffect(() => {
    // Only load saved filters if there's no search query
    if (!searchQuery) {
      try {
        const saved = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
        if (saved) {
          const parsed = JSON.parse(saved);
          setPersistedFacetFilters(parsed);
        }
      } catch {
        // ignore parsing errors
      }
    }
  }, [storageKey, searchQuery]);

  // Save current filters to localStorage whenever they change (only for non-search pages)
  useEffect(() => {
    try {
      if (!currentRefinements || searchQuery) return; // Don't save filters during search

      const groupedByAttribute = currentRefinements
        .map((facetItem: any) =>
          (facetItem?.refinements || []).map((ref: any) => `${facetItem.attribute}:${ref.value}`)
        )
        .filter((arr: any) => Array.isArray(arr) && arr.length > 0);

      if (groupedByAttribute.length > 0) {
        window.localStorage.setItem(storageKey, JSON.stringify(groupedByAttribute));
      } else {
        window.localStorage.removeItem(storageKey);
      }
    } catch {}
  }, [currentRefinements, storageKey, searchQuery]);

  // Restore saved filters from localStorage (only for non-search pages)
  useEffect(() => {
    if (hasHydratedRefinements.current || searchQuery) return; // Don't restore filters during search

    const saved = persistedFacetFilters as any;
    const hasSaved = Array.isArray(saved) && saved.length > 0;

    if (hasSaved) {
      try {
        const currentTokens = new Set<string>(
          (Array.isArray(currentRefinements) ? currentRefinements : [])
            .flatMap((facetItem: any) =>
              (facetItem?.refinements || []).map((ref: any) => `${facetItem.attribute}:${ref.value}`)
            )
        );

        const groups: string[][] = saved as any;
        const refinementList: Record<string, string[]> = {};

        for (const group of groups) {
          for (const token of group) {
            if (currentTokens.has(token)) continue;
            const [attribute, ...rest] = String(token).split(':');
            const value = rest.join(':');
            if (!attribute || value === undefined) continue;
            if (!refinementList[attribute]) refinementList[attribute] = [];
            if (!refinementList[attribute].includes(value)) refinementList[attribute].push(value);
          }
        }

        if (Object.keys(refinementList).length > 0) {
          setUiState((prev: any) => ({
            ...prev,
            [productsIndex]: {
              ...((prev || {})[productsIndex] || {}),
              refinementList: {
                ...((((prev || {})[productsIndex] || {}).refinementList) || {}),
                ...refinementList,
              },
            },
          }));
        }

        hasHydratedRefinements.current = true;
      } catch {}
    } else {
      hasHydratedRefinements.current = true;
    }
  }, [persistedFacetFilters, currentRefinements, setUiState, productsIndex, searchQuery]);

  // Reset hydration flag when storage key changes (new page/category)
  useEffect(() => {
    hasHydratedRefinements.current = false;
  }, [storageKey]);

  // Set loading to false when results are available
  useEffect(() => {
    if (results) {
      setLoading(false);
    }
  }, [results]);  
  const category = useMemo(() => categories.find((category) => category.slug === slug), [categories, slug]);
  const IL_CATEGORY_FILTERS: IndependentLivingCategoriesFilters = {
    'bathroom-accessories': `searchableAttributes.sub-brand:"LiveWell" AND categoryKeys.${localeKey}:"Bathroom Accessories" AND (categoryKeys.${localeKey}:"Care Accessories"  OR searchableAttributes.range:"Opal" OR searchableAttributes.range:"Opal II" OR searchableAttributes.range:"OpalCare" OR searchableAttributes.range:"OPALSUPPORT")`,
    basins: `searchableAttributes.sub-brand:"LiveWell" AND categoryKeys.${localeKey}:"Basins" AND (categoryKeys.${localeKey}:"Care Basins" OR searchableAttributes.range:"Opal" OR searchableAttributes.range:"Opal II" OR searchableAttributes.range:"OpalCare" OR searchableAttributes.range:"OPALSUPPORT")`,
    showers: `searchableAttributes.sub-brand:"LiveWell" AND categoryKeys.${localeKey}:"Showers" AND (categoryKeys.${localeKey}:"Care Showers" OR searchableAttributes.range:"Opal" OR searchableAttributes.range:"Opal II" OR searchableAttributes.range:"OpalCare" OR searchableAttributes.range:"OPALSUPPORT")`,
    toilets: `searchableAttributes.sub-brand:"LiveWell" AND categoryKeys.${localeKey}:"Sanitaryware" AND (categoryKeys.${localeKey}:"Care Toilets" OR searchableAttributes.range:"Opal" OR searchableAttributes.range:"Opal II" OR searchableAttributes.range:"OpalCare" OR searchableAttributes.range:"OPALSUPPORT")`,
    tapware: `searchableAttributes.sub-brand:"LiveWell" AND categoryKeys.${localeKey}:"Tapware" AND (categoryKeys.${localeKey}:"Care Tapware" OR searchableAttributes.range:"Opal" OR searchableAttributes.range:"Opal II" OR searchableAttributes.range:"OpalCare" OR searchableAttributes.range:"OPALSUPPORT")`,
  };
  const replaceLocalePlaceholder = (str: string): string => {
    return str.replace(/LOCALE/g, localeUpperCase);
  };


  //Updated all gwa-product-type to product-types
  const getFilterQuery = () => {
    if (isClark && slug === 'clark')
      return `searchableAttributes.brand:"Clark"`
    if (isDorf && slug === 'dorf')
      return `searchableAttributes.brand:"Dorf"`
    if (isKitchenLaundry && slug === 'tapware')
      return `searchableAttributes.product-types:"Sink Mixers" OR searchableAttributes.product-types:"Retractable Sink Mixers" OR searchableAttributes.product-types:"Laundry Sink Mixer" OR searchableAttributes.product-types:"Washing Machine" OR searchableAttributes.product-types:"Stop Taps" OR searchableAttributes.product-types:"Care Sink Mixers" OR searchableAttributes.product-types:"Sink Outlets" OR searchableAttributes.product-types:"Wall Top Assemblies" OR searchableAttributes.product-types:"Wall Sink Sets" AND NOT categoryKeys.${localeKey}:"Care Tapware" AND NOT searchableAttributes.range:"Opal"`;
    if (isKitchenLaundry && slug === 'kitchen-sinks-accessories')
      return `categoryKeys.${localeKey}:"Kitchen Sinks Accessories" AND NOT categoryKeys.${localeKey}:"Care Sinks"`;
    if (!isKitchenLaundry && !isIndependentLiving && slug === 'tapware')
      return `categoryKeys.${localeKey}:tapware OR categoryKeys.${localeKey}:Tapware AND NOT searchableAttributes.product-types:"Sink Mixers" AND NOT searchableAttributes.product-types:"Retractable Sink Mixers" AND NOT searchableAttributes.product-types:"Laundry Sink Mixer" AND NOT searchableAttributes.product-types:"Washing Machine" AND NOT searchableAttributes.product-types:"Stop Taps" AND NOT searchableAttributes.product-types:"Care Sink Mixers" AND NOT searchableAttributes.product-types:"Sink Outlets" AND NOT searchableAttributes.product-types:"Wall Top Assemblies" AND NOT searchableAttributes.product-types:"Wall Sink Sets" AND NOT categoryKeys.${localeKey}:"Care Tapware" AND NOT searchableAttributes.range:"Opal"`;
    if (isBathroom && slug === 'toilet-suites')
      return `categoryKeys.${localeKey}:Sanitaryware AND NOT categoryKeys.${localeKey}:"Care Toilets" AND NOT searchableAttributes.range:"Opal" AND NOT searchableAttributes.range:"Opal II" AND NOT searchableAttributes.range:"Opal II Easy Height" AND NOT searchableAttributes.range:"care 660" AND NOT searchableAttributes.range:"care 800" AND NOT searchableAttributes.range:"OpalCare"`;
    if (isBathroom && slug === 'showers')
      return `categoryKeys.${localeKey}:showers AND NOT categoryKeys.${localeKey}:"Care Showers" AND NOT searchableAttributes.range:"Opal"`;
    if (isBathroom && slug === 'basins')
      return `categoryKeys.${localeKey}:basins AND NOT categoryKeys.${localeKey}:"Care Basins"`;
    if (isBathroom && slug === 'bathroom-accessories')
      return `categoryKeys.${localeKey}:"Bathroom Accessories" AND NOT categoryKeys.${localeKey}:"Care Accessories"`;
    if (isKitchenLaundry && !category)
      return `categoryKeys.${localeKey}:"Laundry Tubs" OR categoryKeys.${localeKey}:"kitchen sinks accessories" OR searchableAttributes.product-types:"Sink Mixers" OR searchableAttributes.product-types:"Retractable Sink Mixers" OR searchableAttributes.product-types:"Laundry Sink Mixer" OR searchableAttributes.product-types:"Washing Machine" OR searchableAttributes.product-types:"Stop Taps" OR searchableAttributes.product-types:"Care Sink Mixers" OR searchableAttributes.product-types:"Sink Outlets" OR searchableAttributes.product-types:"Wall Top Assemblies" OR searchableAttributes.product-types:"Wall Sink Sets"  AND NOT categoryKeys.${localeKey}:"Care Sinks" AND NOT categoryKeys.${localeKey}:"Care Tapware" AND NOT searchableAttributes.range:"Opal"`;
    if (isBathroom && !category)
      return `categoryKeys.${localeKey}:Tapware OR categoryKeys.${localeKey}:Basins OR categoryKeys.${localeKey}:"Basin Spare Parts" OR categoryKeys.${localeKey}:"Bath Spare Parts" OR categoryKeys.${localeKey}:"Bathroom Accessories" OR categoryKeys.${localeKey}:Baths OR categoryKeys.${localeKey}:"Shower Spare Parts" OR categoryKeys.${localeKey}:Showers OR categoryKeys.${localeKey}:"Toilet Spare Parts" OR categoryKeys.${localeKey}:Sanitaryware OR categoryKeys.${localeKey}:"Valves Fittings" OR categoryKeys.${localeKey}:Tapware AND NOT searchableAttributes.product-types:"Sink Mixers" AND NOT searchableAttributes.product-types:"Retractable Sink Mixers" AND NOT searchableAttributes.product-types:"Laundry Sink Mixer" AND NOT searchableAttributes.product-types:"Washing Machine" AND NOT searchableAttributes.product-types:"Stop Taps" AND NOT searchableAttributes.product-types:"Care Sink Mixers" AND NOT searchableAttributes.product-types:"Sink Outlets" AND NOT searchableAttributes.product-types:"Wall Top Assemblies" AND NOT searchableAttributes.product-types:"Wall Sink Sets"  AND NOT categoryKeys.${localeKey}:"Care Sinks" AND NOT categoryKeys.${localeKey}:"Care Basins" AND NOT categoryKeys.${localeKey}:"Care Accessories" AND NOT categoryKeys.${localeKey}:"Care Tapware" AND NOT categoryKeys.${localeKey}:"Care Showers" AND NOT categoryKeys.${localeKey}:"Care Toilets" AND NOT searchableAttributes.range:"Opal" AND NOT searchableAttributes.range:"Opal II" AND NOT searchableAttributes.range:"Opal II Easy Height" AND NOT searchableAttributes.range:"care 660" AND NOT searchableAttributes.range:"care 800" AND NOT searchableAttributes.range:"OpalCare"`;
    if (isIndependentLiving && category?.categoryId !== undefined) {
      const key = category.categoryId as keyof IndependentLivingCategoriesFilters;
      return IL_CATEGORY_FILTERS[key];
    }
    if (isIndependentLiving && slug === 'grab-rails')
      return `categoryKeys.${localeKey}:"Bathroom Accessories" AND searchableAttributes.product-types:"Grab Rails" AND searchableAttributes.sub-brand:"LiveWell"`;
    if (isIndependentLiving && !category && slug === 'toilets')
      return `searchableAttributes.sub-brand:"LiveWell" AND categoryKeys.${localeKey}:"Sanitaryware" AND (categoryKeys.${localeKey}:"Care Toilets" OR searchableAttributes.range:"Opal" OR searchableAttributes.range:"Opal II" OR searchableAttributes.range:"OpalCare" OR searchableAttributes.range:"OPALSUPPORT")`;
    if (isIndependentLiving && !category)
      return `categoryKeys.${localeKey}:"Care Sinks" OR categoryKeys.${localeKey}:"Care Tapware" OR categoryKeys.${localeKey}:"Care Showers" OR categoryKeys.${localeKey}:"Care Toilets" OR categoryKeys.${localeKey}:"Care Accessories" OR categoryKeys.${localeKey}:"Care Basins" OR searchableAttributes.range:opal OR searchableAttributes.range:"Opal II Easy Height"`;
    if (!!category) return `categoryKeys.${localeKey}:"${category.categoryId?.replaceAll('-', ' ')}"`;
  };
  const getTitleFromSlug = () => {
    if (slug === 'grab-rails') return 'Grab Rails';
    if (slug === 'toilets') return 'Toilets';
    if (slug === 'bathroom') return 'Bathroom';
    if (slug === 'basins') return 'Basins';
    if (slug === 'kitchen-sinks-accessories') return 'Kitchen Sinks & Accessories';
    if (slug === 'bathroom-accessories') return 'Bathroom Accessories';
    if (slug === 'showers') return 'Showers';
    if (slug === 'baths') return 'Baths';
    if (slug === 'laundry-tubs') return 'Laundry Tubs';
    if (slug === 'tapware') return 'Tapware & Mixers';
    if (slug === 'kitchen-laundry') return 'Kitchen & Laundry';
    if (slug === 'independent-living') return 'Independent Living & Care';
    if (slug === 'toilet-suites') return 'Toilets';
    if (slug === 'clark') return 'Clark';
    if (slug === 'dorf') return 'Dorf';
    if (slug === 'spare-parts') return 'Spare Parts';
    return `${slug?.replaceAll('-', ' ').replace(' ', ' & ')}`;
  };

  const handleChange = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof HTMLAnchorElement) {
      const element = document.getElementById("plp-container");
      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth'
        });
      }
    }
  };


  // typing workaround for Configure props in this context
  const ConfigureSearch: any = Configure;

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLastPage) {
          showMore();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => {
      observer.disconnect();
    };
  }, [isLastPage, showMore, currentView]);


  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
        <p className="text-gray-600 text-lg font-medium">
          {searchQuery ? 'Searching products...' : 'Loading products...'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div id="plp-container" className={searchQuery ? 'bg-white' : 'bg-[#F3EDE9]'}>
        <ConfigureSearch
          query={searchQuery ?? ''}
          filters={getFilterQuery()}
          hitsPerPage={12}
          facets={['*']}
          attributesToHighlight={[]} // Disable highlighting
          attributesToSnippet={[]} // Disable snippetting
          attributesToRetrieve={[
            "name",
            "slug",
            "categoryKeys",
            "masterVariantID",
            "searchableAttributes.IsNew",
            "variants.id",
            "variants.key",
            "variants.sku",
            "variants.images",
            "variants.attributes.colour",
            "variants.attributes.item-code",
            "variants.attributes.item-name",
            "variants.prices",
            "variants.attributes.variant-name"
          ]}
          maxValuesPerFacet={999}
        />
        {searchQuery ? (
          <div className="mt-[1rem] px-24 lg:mt-[0.75rem] lg:px-48 xl:px-60 2xl:mt-[2rem] 2xl:px-80">
            <SearchHeader
              query={searchQuery ?? ''}
              categories={categories}
              noContentSuggestions={noContentSuggestions}
            />
          </div>
        ) : (
          <>
            <div id="container-scroll" className="flex flex-col items-end">
              <h1 className="w-full pb-32 pt-48 text-center text-32 font-medium leading-8 text-[#090909] md:text-40 2xl:pb-48 2xl:pt-80 2xl:text-48">
                Products in {getTitleFromSlug()}
              </h1>
              <div className="px:24 mt-8 flex w-full items-center justify-between gap-48 lg:px-48 xl:px-60 2xl:px-80">
                <div className="lg:ml-[16.5rem]  2xl:ml-[23rem]">
                  <CurrentRefinements />
                </div>
                <div className="hidden lg:flex items-center gap-4">
                  <DesktopSortFacets />
                  <ViewToggle onViewChange={handleViewChange} />
                </div>
              </div>
            </div>
          </>
        )}
        <div
          className={`items-start gap-24 pt-24 md:flex md:flex-row lg:px-48 xl:px-60 2xl:gap-48 2xl:px-80 ${
            searchQuery ? '' : 'px-24 md:px-28'
          } ${results?.nbHits === 0 && items?.length ===0? 'pb-0' : 'pb-48 xl:pb-0 2xl:pb-48'}`}
        >
          <div
            className={
              searchQuery
                ? `${
                    results?.nbHits === 0&& items?.length ===0 ? '' : 'lg:block'
                  } hidden bg-white lg:mt-40  lg:min-w-[15rem]  2xl:min-w-[20rem]`
                : `${
                    results?.nbHits === 0 && items?.length ===0? '' : 'lg:block'
                  }  hidden bg-[#F3EDE9] lg:block lg:min-w-[15rem] 2xl:min-w-[20rem]`
            }
          >
            <DesktopFacets facetsConfiguration={facetsConfiguration} />
          </div>
          <div className={searchQuery ? 'w-full flex-1  bg-white lg:mt-40' : 'w-full bg-[#F3EDE9]'}>
            {searchQuery && (
              <h1 className="px-24 py-12 text-32 font-medium leading-8 text-[#090909] md:py-32  md:px-28 md:text-40 lg:hidden lg:py-0  2xl:text-48">
                Products
              </h1>
            )}
            {searchQuery && (
              <div className=''>
              <div id="product-suggestions" className="pb-10 md:pb-24 lg:pb-0 2xl:pb-0 xl:pb-0 flex items-start" >
                <CurrentRefinements />
                {isTabletSize ? (
                  <NoResultsBoundary fallback={<span></span>}>
                    <div className="ml-auto flex flex-col items-end">
                      <DesktopSortFacets />
                    </div>
                  </NoResultsBoundary>
                ) : (
                  <></>
                )}
                </div>
              </div>
            )}

            <div
              className={`${searchQuery ? 'px-24 md:px-28' : ''} ${
                results?.nbHits === 0 && items?.length === 0 && 'hidden'
              } flex items-center justify-between gap-2 lg:block`}
            >
              <div className="block lg:hidden">
                <div className="cursor-pointer py-4 text-[14px] bg-transparent">
                  <ViewToggle onViewChange={handleViewChange} />
                </div>
              </div>
              <MobileFacets facetsConfiguration={facetsConfiguration} />
            </div>


            {isTabletSize ? (
              <>
                <NoResultsBoundary fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}>
                  {currentView === 'list' && (
                    <div className="overflow-x-auto">
                      <div className="hidden lg:flex font-semibold text-gray-800 text-[15px]">
                        <div className="px-6 py-4 w-[60%] text-left">Product</div>
                        <div className="px-6 py-4 w-[20%] text-left">Code</div>
                        <div className="px-6 py-4 w-[20%] text-left">RRP</div>
                      </div>
                    </div>
                  )}

                  <InfiniteHits
                    showPrevious={false}
                    hitComponent={({ hit }: any) => (
                      <ProductTile
                        images={hit?.images}
                        product={mapProduct(hit, locale)}
                        isSearchResult={!!searchQuery}
                        isCategoryPage={true}
                        globalView={currentView}
                      />
                    )}
                    classNames={{
                      root:
                        currentView === 'grid'
                          ? searchQuery
                            ? 'px-24 md:px-28 pt-32'
                            : 'pt-32 lg:pt-0'
                          : searchQuery
                            ? 'pt-32'
                            : 'pt-32 lg:pt-0',

                      list:
                        currentView === 'grid'
                          ? 'grid grid-cols-1 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-16 [&>div]:aspect-[3/4]'
                          : 'flex flex-col',

                      loadMore: '!hidden',
                    }}
                    translations={{
                      showMoreButtonText: formatProductMessage({
                        id: 'show.more',
                        defaultMessage: 'Show More',
                      }),
                    }}
                  />
                  <div ref={sentinelRef} className="h-10"></div>

                </NoResultsBoundary>
              </>
            ) : (
              <NoResultsBoundary fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}>
                <InfiniteHits
                  showPrevious={false}
                  hitComponent={({ hit }: any) => (
                    <ProductTile
                      images={hit?.images}
                      product={mapProduct(hit, locale)}
                      isSearchResult={!!searchQuery}
                      isCategoryPage={true}
                      globalView = {globalView}
                    />
                  )}
                  classNames={{
                    root: searchQuery ? 'px-24 md:px-28 pt-32' : 'pt-32 lg:pt-0',
                    list: 'grid md:grid-cols-2 xl:grid-cols-3 gap-16 ',
                    loadMore: '!hidden',
                  }}
                  translations={{
                    showMoreButtonText: formatProductMessage({ id: 'show.more', defaultMessage: 'Show More' }),
                  }}
                />
                <div ref={sentinelRef} className="h-10"></div>
              </NoResultsBoundary>
            )}
          </div>
        </div>
      </div>
      <ScrollToTopButton />
    </>
  );
};
export default ProductListAlgolia;